import { Injectable } from '@angular/core';

export const HRA_CONSTRAINTS = {
  basicSalary: { min: 1_000, max: 10_000_000, step: 1_000 },
  hraReceived: { min: 0,     max: 10_000_000, step: 1_000 },
  rentPaid:    { min: 0,     max: 10_000_000, step: 1_000 }
} as const;

/**
 * Cities qualifying for the 50% (metro) HRA exemption limit under Section 10(13A) / Rule 2A,
 * effective FY 2026-27 onwards (expanded from the earlier 4-city list to include Bangalore,
 * Pune, Hyderabad and Ahmedabad).
 */
export const HRA_METRO_CITIES = [
  'Delhi', 'Mumbai', 'Kolkata', 'Chennai', 'Bangalore', 'Pune', 'Hyderabad', 'Ahmedabad'
] as const;

/** Catch-all for any city outside the metro list; uses the 40% (non-metro) limit. */
export const HRA_NON_METRO_CITIES = ['Other City (Non-Metro)'] as const;

export interface HraResult {
  actualHra: number;
  rentMinusTenPercent: number;
  percentOfSalary: number;
  exemptHra: number;
  taxableHra: number;
  limitingFactor: 'actualHra' | 'rentMinusTenPercent' | 'percentOfSalary';
  annualExemptHra: number;
  annualTaxableHra: number;
}

@Injectable({ providedIn: 'root' })
export class HraCalculatorService {

  /** All inputs are monthly figures. Exemption per Section 10(13A) is the least of the three. */
  calculate(monthlyBasicSalary: number, monthlyHraReceived: number, monthlyRentPaid: number, isMetro: boolean): HraResult {
    const actualHra = monthlyHraReceived;
    const rentMinusTenPercent = Math.max(0, monthlyRentPaid - 0.1 * monthlyBasicSalary);
    const percentOfSalary = (isMetro ? 0.5 : 0.4) * monthlyBasicSalary;

    const exemptHra = Math.min(actualHra, rentMinusTenPercent, percentOfSalary);
    const taxableHra = actualHra - exemptHra;

    let limitingFactor: HraResult['limitingFactor'] = 'actualHra';
    if (exemptHra === rentMinusTenPercent && rentMinusTenPercent <= actualHra && rentMinusTenPercent <= percentOfSalary) {
      limitingFactor = 'rentMinusTenPercent';
    } else if (exemptHra === percentOfSalary && percentOfSalary <= actualHra && percentOfSalary <= rentMinusTenPercent) {
      limitingFactor = 'percentOfSalary';
    }

    return {
      actualHra, rentMinusTenPercent, percentOfSalary, exemptHra, taxableHra, limitingFactor,
      annualExemptHra: exemptHra * 12,
      annualTaxableHra: taxableHra * 12
    };
  }
}
